import { z } from "zod";
import type { MissionDetail } from "./mission-api.js";

export const WORKSPACE_RESULT_SUFFIX = ".andrew.json";
export const WORKSPACE_RESULT_MAX_BYTES = 64 * 1024;
const id = z.string().min(1).max(100);

/** Versioned presentation saved as an ordinary private artifact, never an executable app. */
const missionSource = z
  .object({
    id,
    planRevision: z.number().int().nonnegative(),
    evidenceIds: z
      .array(id)
      .min(1)
      .max(32)
      .refine((ids) => new Set(ids).size === ids.length),
  })
  .strict();
const resultFields = {
  format: z.literal("andrew-review"),
  title: z.string().trim().min(1).max(160),
  summary: z.string().trim().min(1).max(4000),
};
const source = z.string().min(1).max(32_000);
export const WorkspaceSourceRefSchema = z
  .object({ kind: z.enum(["file", "message"]), id })
  .strict();
export const WorkspaceSourceRefsSchema = z
  .array(WorkspaceSourceRefSchema)
  .min(1)
  .max(16)
  .refine((refs) => new Set(refs.map((ref) => ref.id)).size === refs.length);
export const ResolvedWorkspaceSourceSchema = WorkspaceSourceRefSchema.extend({
  available: z.boolean(),
  title: z.string().optional(),
  revision: z.string().optional(),
  latest: z.boolean().optional(),
  webPath: z.string().optional(),
  mobilePath: z.string().optional(),
}).strict();
export type WorkspaceSourceRef = z.infer<typeof WorkspaceSourceRefSchema>;
export type ResolvedWorkspaceSource = z.infer<typeof ResolvedWorkspaceSourceSchema>;
export const WorkspaceResultSchema = z.discriminatedUnion("version", [
  z
    .object({
      ...resultFields,
      version: z.literal(1),
      mission: missionSource,
      presentation: z.object({ library: z.literal("andrew-review-v1"), source }).strict(),
    })
    .strict(),
  z
    .object({
      ...resultFields,
      version: z.literal(2),
      mission: missionSource
        .extend({
          decisionIds: z
            .array(id)
            .max(8)
            .refine((ids) => new Set(ids).size === ids.length)
            .default([]),
        })
        .strict(),
      presentation: z.object({ library: z.literal("andrew-review-v2"), source }).strict(),
    })
    .strict(),
  z
    .object({
      ...resultFields,
      version: z.literal(3),
      sources: z
        .array(
          WorkspaceSourceRefSchema.extend({
            revision: z.string().regex(/^[a-f0-9]{64}$/),
          }).strict(),
        )
        .min(1)
        .max(16)
        .refine((refs) => new Set(refs.map((ref) => ref.id)).size === refs.length),
      presentation: z.object({ library: z.literal("andrew-result-v3"), source }).strict(),
    })
    .strict(),
]);
export type MissionWorkspaceResult = Exclude<z.infer<typeof WorkspaceResultSchema>, { version: 3 }>;
export type SourcedWorkspaceResult = Extract<z.infer<typeof WorkspaceResultSchema>, { version: 3 }>;
export type WorkspaceResult = z.infer<typeof WorkspaceResultSchema>;

export function isWorkspaceResult(name: string, mimeType: string) {
  return mimeType === "application/json" && name.toLowerCase().endsWith(WORKSPACE_RESULT_SUFFIX);
}
export function readWorkspaceResult(text: string): WorkspaceResult {
  if (
    text.length > WORKSPACE_RESULT_MAX_BYTES ||
    new TextEncoder().encode(text).byteLength > WORKSPACE_RESULT_MAX_BYTES
  )
    throw new Error("Review exceeds its size limit.");
  return WorkspaceResultSchema.parse(JSON.parse(text));
}

/** Current source availability is separate from whether the agent's conclusions are correct. */
export function workspaceResultSources<
  E extends Pick<MissionDetail["evidence"][number], "id" | "stale">,
>(
  result: WorkspaceResult,
  mission: Pick<MissionDetail, "id" | "planRevision"> & {
    evidence: E[];
  },
) {
  if (result.version === 3 || mission.id !== result.mission.id)
    return { state: "unavailable" as const, evidence: [] };
  const evidence = result.mission.evidenceIds.flatMap(
    (id) => mission.evidence.find((item) => item.id === id) ?? [],
  );
  if (evidence.length !== result.mission.evidenceIds.length)
    return { state: "unavailable" as const, evidence };
  const changed =
    mission.planRevision !== result.mission.planRevision || evidence.some((item) => item.stale);
  return { state: changed ? ("changed" as const) : ("current" as const), evidence };
}

/** Generated files choose an existing decision, never its label, target or authority. */
export function workspaceResultDecision(
  result: WorkspaceResult,
  mission: MissionDetail,
  decisionId: string,
) {
  if (result.version !== 2 || !result.mission.decisionIds.includes(decisionId)) return null;
  if (workspaceResultSources(result, mission).state !== "current") return null;
  const decision = mission.decisions.find((item) => item.id === decisionId);
  if (
    !decision ||
    decision.planRevision !== mission.planRevision ||
    ["stale", "superseded"].includes(decision.status) ||
    Object.entries(decision.sourceRevisions).some(
      ([key, value]) => mission.plan?.sourceRevisions[key] !== value,
    )
  )
    return null;
  return decision;
}

export function workspaceResultSourceIds(result: WorkspaceResult) {
  return result.version === 3
    ? result.sources.map((source) => source.id)
    : result.mission.evidenceIds;
}
export function sourcedWorkspaceResultState(
  result: SourcedWorkspaceResult,
  current: ResolvedWorkspaceSource[],
) {
  const sources = result.sources.map((ref) =>
    current.find((source) => source.id === ref.id && source.kind === ref.kind),
  );
  if (sources.some((source) => !source?.available)) return "unavailable" as const;
  return sources.some(
    (source, i) => source!.revision !== result.sources[i]!.revision || source!.latest === false,
  )
    ? ("changed" as const)
    : ("current" as const);
}

export const WORKSPACE_RESULT_GUIDE = {
  filename: "review-<mission-id>.andrew.json",
  mimeType: "application/json",
  instructions:
    "For mission reviews, read the mission and actual evidence first. Save a UTF-8 JSON file using this schema, then attach_file it with the same exact filename on every correction. Alternatively use artifacts.create with the same target and name, and a fresh clientNonce per revision. This creates artifact versions, not new missions. Source IDs must come from the mission; preserve its current planRevision. Summary is the readable fallback. Presentation source uses OpenUI Lang: root = Review([Section(heading, body, evidenceIds), Compare(heading, columns, [Row(cells), ...], evidenceIds), Bars(heading, unit, [Bar(label, numericText), ...], evidenceIds), Evidence(evidenceId), Decision(decisionId)]). Use only components that help the task. Compare has 2–5 columns and 1–12 rows with the same cell count. Bars has 1–12 nonnegative decimal values as quoted strings, with an explicit unit. Maximum 12 blocks; headings 160 characters, section body 4000, cells 500. Evidence IDs must be declared in mission.evidenceIds; decision IDs must be real current server decisions declared in mission.decisionIds. Evidence previews and action labels come from authorized server records, never generated URLs or action payloads. No HTML, executable code, links, state, queries or functions. All displayed values must come from the cited evidence; do not invent comparisons or imply agent-authored numbers are independently verified. Agent conclusions never establish checks passed or mission completion. Return the saved artifact link and register it as mission evidence only through existing authorized mission actions.",
  sourceInstructions:
    "For a result sourced from files or conversations without a mission, use version 3 and library andrew-result-v3. Read the actual source content first through artifacts.getById or threads.messages. Call workspace.resultSources with sources [{kind: file or message, id: actual artifact or message ID}] to obtain server revisions; copy each returned revision into sources. Unavailable records cannot support a result. Use Section, Compare, Row, Bars and Bar only; cite source IDs in their evidenceIds arguments. No Evidence, Decision or executable actions are allowed. A revision proves source identity/freshness, never the accuracy of your interpretation. Preserve the exact output filename and target on corrections to create versions of the same artifact. Never claim a file is current if a newer version exists.",
  schema: z.toJSONSchema(WorkspaceResultSchema),
  example: {
    format: "andrew-review",
    version: 2,
    title: "Release review",
    summary: "The change needs review.",
    mission: { id: "mission-id", planRevision: 1, evidenceIds: ["evidence-id"], decisionIds: [] },
    presentation: {
      library: "andrew-review-v2",
      source:
        'root = Review([Section("What changed", "Describe the observed change and any remaining uncertainty.", ["evidence-id"])])',
    },
  },
};
